import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';

const skeletonStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .skeleton-box {
    display: block;
    position: relative;
    overflow: hidden;
    background-color: #f0f0f0;
    background-image: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-repeat: no-repeat;
    background-size: 1000px 100%;
    animation: shimmer 2s infinite linear;
    border-radius: 4px;
  }
  .client-search-dropdown {
    max-height: 250px;
    overflow-y: auto;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
    z-index: 1050;
  }
`;

const EDO_LGAS = [
  'Akoko-Edo', 'Egor', 'Esan Central', 'Esan North-East', 'Esan South-East',
  'Esan West', 'Etsako Central', 'Etsako East', 'Etsako West', 'Igueben',
  'Ikpoba-Okha', 'Oredo', 'Orhionmwon', 'Ovia North-East', 'Ovia South-West',
  'Owan East', 'Owan West', 'Uhunmwonde'
];

const toProperCase = (text = '') => {
  if (!text) return '';
  const lowerCaseWords = new Set([
    'of', 'and', 'or', 'as', 'in', 'on', 'at', 'to', 'for', 'by', 'with', 'via', 'vs', 'etc', 'the', 'a', 'an'
  ]);
  return text
    .toLowerCase()
    .split(' ')
    .map((word, index, arr) => {
      if (index !== 0 && index !== arr.length - 1 && lowerCaseWords.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const AdManagement = () => {
  const BASE_URL = 'https://adera-web-api.azurewebsites.net/v1';
  const GOOGLE_MAPS_API_KEY = "AIzaSyDh9DjKVMvqiIS7ie4Unsa84sy_7XjXcKE";

  const mapContainerStyle = { width: '100%', height: '100%', minHeight: '600px', borderRadius: '0.625rem' };
  const center = { lat: 6.5244, lng: 6.0000 };
  const edoBounds = { north: 7.5, south: 5.5, west: 5.0, east: 6.8 };

  const [loading, setLoading] = useState(true);
  const [ads, setAds] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [globalAdCount, setGlobalAdCount] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, count: 100, totalPages: 1, totalItems: 0 });
  const [filters, setFilters] = useState({ search: '', paymentStatus: '', partyType: '' });
  const [permitData, setPermitData] = useState(null);
  const [showPermitModal, setShowPermitModal] = useState(false);

  const [showCombinedModal, setShowCombinedModal] = useState(false);
  const [savingAd, setSavingAd] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [isExistingClient, setIsExistingClient] = useState(false);
  const [newClientCategory, setNewClientCategory] = useState('Individual');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [isSearchingClient, setIsSearchingClient] = useState(false);
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [lookups, setLookups] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);

  const [adData, setAdData] = useState({
    title: '', description: '', imageFile: null, imagePreview: null,
    address: '', price: 0,
    zoneId: '', zoneName: '',
    categoryId: '', categoryName: '',
    typeId: '', typeName: '',
    sizeName: '',
    lookUpId: '',
    partyType: 'First Party', faces: 1, duration: 1,
    lga: '', longitude: '', latitude: '', altitude: '',
    selectedClientId: '', selectedClientData: null,
    firstName: '', lastName: '', email: '', phone: '', gender: 'Male',
    companyName: '', companyAddress: '', companyType: 'Others'
  });

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' })
      .format(amount || 0).replace('NGN', '₦');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getDaysAgo = (dateString) => {
    if (!dateString) return '0';
    const diffTime = Math.abs(new Date() - new Date(dateString));
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const checkPaymentStatus = (paymentDate, status) => {
    if (status === 'Failed') return { isPaid: false, isExpired: false, percent: 100, status: 'Failed' };
    if (!paymentDate) return { isPaid: false, isExpired: false, percent: 0, status: 'Pending' };
    const payDate = new Date(paymentDate);
    const oneYearLater = new Date(payDate);
    oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
    const isExpired = new Date() > oneYearLater;
    const percent = Math.min(100, Math.max(0, ((new Date() - payDate) / (oneYearLater - payDate)) * 100));
    return { isPaid: true, isExpired, percent, status: isExpired ? 'Expired' : 'Active' };
  };

  const getClientName = (ad) => {
    if (ad.client?.company) return ad.client.company.name;
    if (ad.client?.individual) return `${ad.client.individual.firstName} ${ad.client.individual.lastName}`;
    return '';
  };

  const getClientEmail = (ad) => {
    if (ad.client?.company) return ad.client.company.email;
    if (ad.client?.individual) return ad.client.individual.email;
    return 'N/A';
  };

  const getClientPhone = (ad) => {
    if (ad.client?.company) return ad.client.company.phoneNumber;
    if (ad.client?.individual) return ad.client.individual.phoneNumber;
    return 'N/A';
  };

  const getMeasurementDisplay = (ad) => {
    const measurement = ad.measurement || 1;
    const unit = ad.lookup?.measurementUnit?.name || 'unit';
    return `${measurement} ${unit}${measurement > 1 ? 's' : ''}`;
  };

  const getToken = () => localStorage.getItem('token');
  const authHeader = () => ({ 'Authorization': `Bearer ${getToken()}` });

  const fetchModalData = async () => {
    try {
      const locRes = await fetch(`${BASE_URL}/utils/ad/location?all=true`, { headers: authHeader() });
      const locResult = await locRes.json();
      if (locResult.ok) setLocations(locResult.data.pageData || []);

      const catRes = await fetch(`${BASE_URL}/utils/ad/category?all=true`, { headers: authHeader() });
      const catResult = await catRes.json();
      if (catResult.ok) setCategories(catResult.data.pageData || []);

      const lookupRes = await fetch(`${BASE_URL}/admin/ad/lookup?all=true`, { headers: authHeader() });
      const lookupResult = await lookupRes.json();
      if (lookupResult.ok) setLookups(lookupResult.data.pageData || []);
    } catch (error) {
      console.error('Error fetching modal data:', error);
    }
  };

  const fetchTypesForCategory = async (categoryId) => {
    if (!categoryId) { setTypes([]); return; }
    try {
      const res = await fetch(`${BASE_URL}/utils/ad/category/${categoryId}/type?all=true`, { headers: authHeader() });
      const result = await res.json();
      if (result.ok) setTypes(result.data.pageData || []);
    } catch (error) { console.error(error); }
  };

  useEffect(() => {
    if (!adData.zoneName || !adData.categoryName || !adData.typeName) {
      setAvailableSizes([]);
      return;
    }
    const normalize = (s) => (s || '').toLowerCase().trim();
    const matched = lookups.filter(l =>
      normalize(l.location?.name) === normalize(adData.zoneName) &&
      normalize(l.category?.name) === normalize(adData.categoryName) &&
      normalize(l.type?.name) === normalize(adData.typeName)
    );
    const uniqueSizes = [...new Map(matched.map(l => [l.size?.name, l.size?.name])).values()].filter(Boolean);
    setAvailableSizes(uniqueSizes);
  }, [adData.zoneName, adData.categoryName, adData.typeName, lookups]);

  const calculatePriceAndLookup = useCallback((data = adData) => {
    if (!data.zoneName || !data.categoryName || !data.typeName || !data.sizeName) return null;
    const normalize = (s) => (s || '').toLowerCase().trim();
    const match = lookups.find(l =>
      normalize(l.location?.name) === normalize(data.zoneName) &&
      normalize(l.category?.name) === normalize(data.categoryName) &&
      normalize(l.type?.name) === normalize(data.typeName) &&
      normalize(l.size?.name) === normalize(data.sizeName)
    );
    return match || null;
  }, [lookups, adData]);

  useEffect(() => {
    const match = calculatePriceAndLookup();
    const total = match ? (match.price || 0) * adData.faces * adData.duration : 0;
    const lookUpId = match ? match.id : '';

    let title = adData.sizeName ? `${adData.sizeName} ${adData.typeName || ''}`.trim() : '';
    let description = `Advertisement at ${adData.zoneName || 'Location'}`;

    if (isExistingClient && adData.selectedClientData) {
      const name = adData.selectedClientData.company?.name ||
        `${adData.selectedClientData.individual?.firstName || ''} ${adData.selectedClientData.individual?.lastName || ''}`.trim();
      if (name) description += ` for ${name}`;
    } else if (!isExistingClient) {
      if (newClientCategory === 'Individual' && adData.firstName) description += ` for ${adData.firstName} ${adData.lastName}`.trim();
      else if (newClientCategory === 'Company' && adData.companyName) description += ` for ${adData.companyName}`;
    }

    setAdData(prev => ({ ...prev, price: total, lookUpId, title, description }));
  }, [adData.zoneName, adData.categoryName, adData.typeName, adData.sizeName, adData.faces, adData.duration,
      adData.selectedClientData, adData.firstName, adData.lastName, adData.companyName, lookups]);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => setAdData(prev => ({ ...prev, imageFile: file, imagePreview: reader.result }));
        reader.readAsDataURL(file);
      }
      return;
    }

    if (name === 'zoneId') {
      const loc = locations.find(l => l.id === value);
      setAdData(prev => ({ ...prev, zoneId: value, zoneName: loc?.name || '', categoryId: '', categoryName: '', typeId: '', typeName: '', sizeName: '', lookUpId: '' }));
      setTypes([]);
      return;
    }

    if (name === 'categoryId') {
      const cat = categories.find(c => c.id === value);
      setAdData(prev => ({ ...prev, categoryId: value, categoryName: cat?.name || '', typeId: '', typeName: '', sizeName: '', lookUpId: '' }));
      setTypes([]);
      fetchTypesForCategory(value);
      return;
    }

    if (name === 'typeId') {
      const t = types.find(t => t.id === value);
      setAdData(prev => ({ ...prev, typeId: value, typeName: t?.name || '', sizeName: '', lookUpId: '' }));
      return;
    }

    if (name === 'sizeName') {
      setAdData(prev => ({ ...prev, sizeName: value }));
      return;
    }

    setAdData(prev => ({ ...prev, [name]: value }));
  };

  const handleClientSearch = async () => {
    if (!clientSearchQuery || clientSearchQuery.length < 2) return;
    setIsSearchingClient(true);
    try {
      const res = await fetch(`${BASE_URL}/admin/client?search=${encodeURIComponent(clientSearchQuery)}&page=1&count=100`, { headers: authHeader() });
      const result = await res.json();
      if (result.ok) {
        setClientSearchResults(result.data.pageData || []);
        setShowClientDropdown(true);
      }
    } catch (err) { console.error(err); }
    finally { setIsSearchingClient(false); }
  };

  const selectExistingClient = (client) => {
    setAdData(prev => ({ ...prev, selectedClientId: client.id, selectedClientData: client }));
    setShowClientDropdown(false);
    setClientSearchResults([]);
  };

  const resetModal = () => {
    setShowCombinedModal(true);
    fetchModalData();
    setIsExistingClient(false);
    setNewClientCategory('Individual');
    setClientSearchQuery('');
    setTypes([]);
    setAvailableSizes([]);
    setAdData({
      title: '', description: '', imageFile: null, imagePreview: null, address: '', price: 0,
      zoneId: '', zoneName: '', categoryId: '', categoryName: '', typeId: '', typeName: '',
      sizeName: '', lookUpId: '', partyType: 'First Party', faces: 1, duration: 1,
      lga: '', longitude: '', latitude: '', altitude: '',
      selectedClientId: '', selectedClientData: null,
      firstName: '', lastName: '', email: '', phone: '', gender: 'Male',
      companyName: '', companyAddress: '', companyType: 'Others'
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!adData.zoneId) errors.zone = 'Zone is required';
    if (!adData.categoryId) errors.category = 'Category is required';
    if (!adData.typeId) errors.type = 'Type is required';
    if (!adData.sizeName) errors.size = 'Size is required';
    if (!adData.lookUpId) errors.lookUpId = 'No pricing configuration found for selected options';
    if (!adData.lga) errors.lga = 'LGA is required';
    if (!adData.address) errors.address = 'Address is required';

    if (isExistingClient) {
      if (!adData.selectedClientId) errors.client = 'Please search and select a client';
    } else {
      if (newClientCategory === 'Individual') {
        if (!adData.firstName) errors.firstName = 'First Name required';
        if (!adData.lastName) errors.lastName = 'Last Name required';
        if (!adData.phone) errors.phone = 'Phone required';
        if (!adData.email) errors.email = 'Email required';
      } else {
        if (!adData.companyName) errors.companyName = 'Company Name required';
        if (!adData.email) errors.email = 'Email required';
        if (!adData.phone) errors.phone = 'Phone required';
        if (!adData.companyAddress) errors.companyAddress = 'Address required';
      }
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setSavingAd(true);
    try {
      let clientId = adData.selectedClientId;

      if (!isExistingClient) {
        const endpoint = newClientCategory === 'Individual' ? '/admin/client/individual' : '/admin/client/company';
        const payload = newClientCategory === 'Individual'
          ? { firstName: adData.firstName, lastName: adData.lastName, email: adData.email, phoneNumber: adData.phone, gender: adData.gender }
          : { name: adData.companyName, email: adData.email, phoneNumber: adData.phone, address: adData.companyAddress, type: adData.companyType };

        const res = await fetch(`${BASE_URL}${endpoint}`, {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await res.json();
        if (!result.ok) throw new Error(result.error?.message || 'Failed to create client');
        clientId = result.data.id;
      }

      const formData = new FormData();
      formData.append('clientId', clientId);
      formData.append('lookUpId', adData.lookUpId);
      formData.append('title', adData.title);
      formData.append('description', adData.description);
      formData.append('address', adData.address);
      formData.append('measurement', adData.faces);
      formData.append('time', adData.duration);
      formData.append('lga', adData.lga);
      if (adData.latitude) formData.append('latitude', adData.latitude);
      if (adData.longitude) formData.append('longitude', adData.longitude);
      if (adData.altitude) formData.append('altitude', adData.altitude);
      if (adData.imageFile) formData.append('image', adData.imageFile);

      const adRes = await fetch(`${BASE_URL}/admin/ad`, { method: 'POST', headers: authHeader(), body: formData });
      const adResult = await adRes.json();
      if (!adResult.ok) throw new Error(adResult.error?.message || 'Failed to create advertisement');

      alert('Advertisement created successfully!');
      setShowCombinedModal(false);
      fetchAds(1);
    } catch (err) {
      setFormErrors({ submit: err.message });
    } finally {
      setSavingAd(false);
    }
  };

  useEffect(() => {
    const fetchGlobalCount = async () => {
      try {
        const response = await fetch(`${BASE_URL}/admin/ad?page=1&count=1`, { headers: authHeader() });
        const result = await response.json();
        if (result.ok) setGlobalAdCount(result.data.totalItems || 0);
      } catch (error) { console.error(error); }
    };
    fetchGlobalCount();
  }, []);

  const fetchAds = useCallback(async (pageToFetch = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pageToFetch);
      params.append('count', 100);
      params.append('order', 'desc');
      if (filters.search) params.append('search', filters.search);
      if (filters.partyType) params.append('search', filters.partyType);
      if (filters.paymentStatus) {
        if (filters.paymentStatus === 'Paid') params.append('status', 'Successful');
        if (filters.paymentStatus === 'Pending') params.append('status', 'Initialize');
        if (filters.paymentStatus === 'Failed') params.append('status', 'Failed');
      }

      const response = await fetch(`${BASE_URL}/admin/ad?${params.toString()}`, { headers: authHeader() });
      const result = await response.json();

      if (result.ok) {
        setAds(result.data.pageData || []);
        setPagination({ page: result.data.page, count: result.data.count, totalPages: result.data.totalPages, totalItems: result.data.totalItems });
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(() => fetchAds(1), 500);
    return () => clearTimeout(timer);
  }, [filters, fetchAds]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) fetchAds(newPage);
  };

  const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

  const openPermitModal = (ad) => {
    const agentFullName = ad.agent ? `${ad.agent.firstName || ''} ${ad.agent.lastName || ''}`.trim() : 'Unassigned Agent';
    setPermitData({
      clientName: toProperCase(getClientName(ad) || 'Unknown Client'),
      clientEmail: getClientEmail(ad),
      clientPhone: getClientPhone(ad),
      clientAddress: toProperCase(ad.client?.company?.address || ad.client?.individual?.address || 'N/A'),
      clientContact: getClientPhone(ad),
      amount: formatCurrency(ad.price),
      category: toProperCase(ad.lookup?.category?.name || 'N/A'),
      type: toProperCase(ad.lookup?.type?.name || 'N/A'),
      expiryDate: formatDate(ad.paymentDueAt),
      adTitle: toProperCase(ad.title),
      adSize: toProperCase(ad.lookup?.size?.name || 'N/A'),
      adLocation: toProperCase(ad.location?.lga || 'N/A'),
      adAddress: toProperCase(ad.address || 'N/A'),
      measurement: getMeasurementDisplay(ad),
      imageUrl: ad.imageUrl || "assets/bill2.jpg",
      status: ad.status,
      agentName: toProperCase(agentFullName),
      agentEmail: ad.agent?.email || 'N/A',
      agentPhone: ad.agent?.phoneNumber || 'N/A'
    });
    setShowPermitModal(true);
    setSelectedMarker(null);
  };

  const renderSkeletons = () => (
    <div className="d-flex flex-column gap-6">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="card card-hover-border overflow-hidden d-flex flex-lg-row align-items-stretch">
          <div className="flex-shrink-0" style={{ width: '200px', height: '190px' }}>
            <div className="skeleton-box h-100 w-100" style={{ borderRadius: '0.625rem 0 0 0.625rem' }}></div>
          </div>
          <div className="p-5 d-flex flex-column justify-content-between flex-grow-1">
            <div>
              <div className="d-flex justify-content-between mb-4">
                <div className="d-flex flex-column gap-2 w-50">
                  <div className="skeleton-box" style={{ width: '70%', height: '20px' }}></div>
                  <div className="skeleton-box" style={{ width: '40%', height: '14px', opacity: 0.7 }}></div>
                  <div className="skeleton-box mt-1" style={{ width: '50%', height: '12px', opacity: 0.6 }}></div>
                </div>
                <div className="d-flex flex-column align-items-end gap-2">
                  <div className="skeleton-box" style={{ width: '120px', height: '24px' }}></div>
                  <div className="skeleton-box" style={{ width: '90px', height: '14px', opacity: 0.7 }}></div>
                </div>
              </div>
              <div className="mb-4"><div className="skeleton-box" style={{ width: '100%', height: '10px', borderRadius: '10px' }}></div></div>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex gap-2 flex-grow-1">
                  <div className="skeleton-box" style={{ width: '100px', height: '32px', borderRadius: '6px' }}></div>
                  <div className="skeleton-box" style={{ width: '80px', height: '32px', borderRadius: '6px' }}></div>
                  <div className="skeleton-box" style={{ width: '80px', height: '32px', borderRadius: '6px' }}></div>
                </div>
                <div className="skeleton-box" style={{ width: '110px', height: '38px', borderRadius: '6px' }}></div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <style>{skeletonStyles}</style>
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">

          <div id="kt_app_header" className="app-header">
            <div className="app-container container-fluid d-flex align-items-stretch justify-content-between">
              <div className="app-header-logo d-flex align-items-center me-lg-20 gap-2">
                <Link to="/"><img alt="Logo" src="assets/aderalogo.png" className="h-60px" /></Link>
              </div>
              <div className="d-flex align-items-stretch justify-content-between flex-lg-grow-1">
                <div className="d-flex align-items-stretch" id="kt_app_header_menu_wrapper">
                  <div className="app-header-menu app-header-mobile-drawer align-items-stretch">
                    <div className="menu menu-rounded menu-column menu-lg-row menu-active-bg menu-title-gray-600 fw-semibold fs-6 align-items-stretch my-5 px-2">
                      <div className="menu-item me-2"><Link to="/dashboarda" className="menu-link"><span className="menu-title">Home</span></Link></div>
                      <div className="menu-item here show menu-here-bg me-2"><Link to="/ad-management" className="menu-link"><span className="menu-title">Advertisement</span></Link></div>
                      <div className="menu-item me-2"><Link to="/agents" className="menu-link"><span className="menu-title">Agents</span></Link></div>
                      <div className="menu-item me-2"><Link to="/payments" className="menu-link"><span className="menu-title">Payments</span></Link></div>
                    </div>
                  </div>
                </div>
                <div className="app-navbar flex-shrink-0 d-flex align-items-center gap-3">
                  <div className="text-end d-none d-sm-flex flex-column justify-content-center me-3">
                    <span className="text-gray-500 fs-8 fw-bold">Hello</span>
                    <span className="text-gray-800 fs-7 fw-bold">EDSAA Admin</span>
                  </div>
                  <div className="cursor-pointer symbol symbol-circle symbol-40px">
                    <img src="assets/edsal.png" alt="User" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="app-wrapper d-flex flex-column flex-column-fluid" id="kt_app_wrapper">

            <div
              className="d-flex flex-stack flex-wrap flex-md-nowrap card-rounded shadow p-lg-10"
              style={{ background: 'linear-gradient(90deg, #20AA3E 0%, #03A588 100%)', marginLeft: '30px', marginRight: '30px', marginTop: '30px' }}
            >
              <div className="my-2 me-5">
                <div className="fs-2x fw-bold text-white mb-2">
                  <span className="text-white text-decoration-underline"><b>{globalAdCount}</b></span>{' '}
                  <span className="fw-normal">Total Adverts</span>
                </div>
              </div>
              <div className="d-flex gap-3 flex-shrink-0 my-2">
                <button className="btn btn-lg border-2 btn-outline btn-outline-white btn-color-white flex-shrink-0 my-2" onClick={resetModal}>
                  <i className="ki-outline ki-plus fs-2 me-2"></i> Add New Advertisement
                </button>
                <Link to="/payments" className="btn btn-lg border-2 btn-light flex-shrink-0 my-2">
                  <i className="ki-outline ki-wallet fs-2 me-2"></i> View Pending Payments
                </Link>
              </div>
            </div>

            <div className="app-container container-fluid d-flex flex-row-fluid">
              <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
                <div className="d-flex flex-column flex-column-fluid">
                  <div id="kt_app_content" className="app-content flex-column-fluid">
                    <div className="row flex-row-fluid">

                      <div className="col-lg-6">
                        <div className="d-flex flex-stack gap-3 flex-wrap mb-5">
                          <div className="position-relative flex-grow-1">
                            <i className="ki-outline ki-magnifier fs-3 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-500"></i>
                            <input
                              type="text" className="form-control ps-10 w-300px"
                              placeholder="Search advertisement..."
                              name="search" value={filters.search} onChange={handleFilterChange}
                            />
                          </div>
                          <div className="d-flex gap-2 flex-shrink-0">
                            <select className="form-select w-150px" name="partyType" value={filters.partyType} onChange={handleFilterChange}>
                              <option value="">All Types</option>
                              <option value="First Party">First Party</option>
                              <option value="Third Party">Third Party</option>
                            </select>
                          </div>
                        </div>

                        <div className="separator separator-gray-300 my-5"></div>

                        <div className="hover-scroll me-n2 pe-2" style={{ height: '600px', overflowY: 'auto' }}>
                          {loading ? renderSkeletons() : ads.length === 0 ? (
                            <div className="text-center mt-10 text-muted">No advertisements found matching filters.</div>
                          ) : (
                            <div className="d-flex flex-column gap-6">
                              {ads.map((ad) => {
                                const isPaid = ad.status === 'Successful';
                                const paymentCalc = checkPaymentStatus(ad.paymentDueAt, ad.status);
                                let progressWidth = '15%', progressClass = 'bg-warning', statusText = 'Created', statusClass = 'text-warning';
                                if (ad.status === 'Failed') { progressWidth = '100%'; progressClass = 'bg-danger'; statusText = 'Payment Failed'; statusClass = 'text-danger'; }
                                else if (isPaid) {
                                  if (paymentCalc.isExpired) { progressWidth = '100%'; progressClass = 'bg-danger'; statusText = 'Expired'; statusClass = 'text-danger'; }
                                  else { progressWidth = `${paymentCalc.percent}%`; progressClass = 'bg-success'; statusText = 'Active'; statusClass = 'text-success'; }
                                } else if (ad.status === 'Initialize') { statusText = 'Initialized'; }

                                return (
                                  <div key={ad.id} className="card card-hover-border overflow-hidden d-flex flex-lg-row align-items-stretch">
                                    <div className="flex-shrink-0" style={{ width: '200px', height: '190px' }}>
                                      <img src={ad.imageUrl || "assets/bill2.jpg"} alt={ad.title} className="w-100 h-100 object-fit-cover rounded-start"
                                        onError={(e) => { e.target.src = "assets/bill2.jpg"; }} />
                                    </div>
                                    <div className="p-5 d-flex flex-column justify-content-between flex-grow-1">
                                      <div>
                                        <div className="d-flex flex-wrap align-items-start justify-content-between mb-4">
                                          <div className="d-flex align-items-center flex-grow-1 me-4">
                                            <div>
                                              <Link to="#" className="text-gray-800 text-hover-primary fs-4 fw-bold d-block mb-1 text-truncate"
                                                style={{ maxWidth: '300px' }} onClick={(e) => { e.preventDefault(); openPermitModal(ad); }}>
                                                {toProperCase(getClientName(ad))}
                                              </Link>
                                              <span className="text-gray-600 fw-semibold d-block fs-7">{toProperCase(ad.lookup?.category?.name || 'Uncategorized')}</span>
                                              <div className="d-flex gap-2 mt-1">
                                                <span className="text-gray-500 fs-8"><i className="ki-outline ki-sms fs-8 me-1"></i>{getClientEmail(ad)?.toLowerCase()}</span>
                                              </div>
                                              <div className="d-flex gap-2">
                                                <span className="text-gray-500 fs-8"><i className="ki-outline ki-phone fs-8 me-1"></i>{getClientPhone(ad)}</span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="text-end ms-auto">
                                            <div className="text-primary fs-2 fw-bolder">{formatCurrency(ad.price)}</div>
                                            {isPaid
                                              ? <div className={`fs-7 fw-semibold mt-1 ${statusClass}`}>{statusText === 'Expired' ? 'Expired' : 'Expires:'} {formatDate(ad.paymentDueAt)}</div>
                                              : <div className={`fs-7 fw-semibold mt-1 ${statusClass}`}>{statusText}: {getDaysAgo(ad.createdAt)} days ago</div>
                                            }
                                          </div>
                                        </div>
                                        <div className="progress mb-4">
                                          <div className={`progress-bar ${progressClass}`} role="progressbar" style={{ width: progressWidth }}></div>
                                        </div>
                                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                          <div className="d-flex flex-wrap gap-2 flex-grow-1">
                                            <div className="badge badge-light-primary d-flex align-items-center gap-2 px-3 py-2">
                                              <i className="ki-outline ki-geolocation fs-6 text-primary"></i>
                                              <span className="text-gray-700 fw-semibold fs-8 text-truncate" style={{ maxWidth: '150px' }}>
                                                {toProperCase(ad.address || ad.location?.lga || 'Unknown')}
                                              </span>
                                            </div>
                                            <div className="badge badge-light-primary d-flex align-items-center gap-2 px-3 py-2">
                                              <i className="ki-outline ki-category fs-6 text-primary"></i>
                                              <span className="text-gray-700 fw-semibold fs-8">{toProperCase(ad.lookup?.type?.name || 'N/A')}</span>
                                            </div>
                                            <div className="badge badge-light-primary d-flex align-items-center gap-2 px-3 py-2">
                                              <i className="ki-outline ki-abstract-26 fs-6 text-primary"></i>
                                              <span className="text-gray-700 fw-semibold fs-8">{getMeasurementDisplay(ad)}</span>
                                            </div>
                                          </div>
                                          <button className="btn btn-sm btn-success fw-semibold px-4 py-2 flex-shrink-0" onClick={() => openPermitModal(ad)}>
                                            View Details
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {!loading && ads.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center pt-5 pb-5">
                              <div className="text-muted fs-7">Showing page {pagination.page} of {pagination.totalPages} ({pagination.totalItems} total)</div>
                              <div>
                                <button className="btn btn-sm btn-light me-2" disabled={pagination.page === 1} onClick={() => handlePageChange(pagination.page - 1)}>Previous</button>
                                <button className="btn btn-sm btn-light" disabled={pagination.page === pagination.totalPages} onClick={() => handlePageChange(pagination.page + 1)}>Next</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-lg-6">
                        <div className="card shadow-sm overflow-hidden border-0" style={{ height: '100%', minHeight: '600px' }}>
                          <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                            <GoogleMap mapContainerStyle={mapContainerStyle} center={center} zoom={12}
                              options={{ restriction: { latLngBounds: edoBounds, strictBounds: false }, mapTypeControl: false, streetViewControl: false, fullscreenControl: true }}>
                              {ads.map(ad => {
                                if (!ad.location?.latitude || !ad.location?.longitude) return null;
                                const lat = parseFloat(ad.location.latitude);
                                const lng = parseFloat(ad.location.longitude);
                                if (isNaN(lat) || isNaN(lng)) return null;
                                return <Marker key={ad.id} position={{ lat, lng }} onClick={() => setSelectedMarker(ad)} />;
                              })}
                              {selectedMarker && (
                                <InfoWindow
                                  position={{ lat: parseFloat(selectedMarker.location.latitude), lng: parseFloat(selectedMarker.location.longitude) }}
                                  onCloseClick={() => setSelectedMarker(null)}
                                  options={{ pixelOffset: new window.google.maps.Size(0, -40) }}
                                >
                                  <div className="p-4" style={{ width: '320px' }}>
                                    <div className="mb-3 position-relative rounded overflow-hidden" style={{ height: '140px' }}>
                                      <img src={selectedMarker.imageUrl} className="w-100 h-100 object-fit-cover" alt="ad" />
                                      <span className="position-absolute top-0 end-0 badge badge-success m-2">{formatCurrency(selectedMarker.price)}</span>
                                    </div>
                                    <h6 className="fw-bold text-gray-900 mb-2 text-truncate">{toProperCase(getClientName(selectedMarker))}</h6>
                                    <div className="mb-2"><span className="badge badge-light-primary fs-8">{toProperCase(selectedMarker.lookup?.category?.name || 'N/A')}</span></div>
                                    <div className="d-flex align-items-center text-muted fs-7 mb-2">
                                      <i className="ki-outline ki-geolocation fs-6 me-1"></i>
                                      <span className="text-truncate" style={{ maxWidth: '220px' }}>{toProperCase(selectedMarker.address || selectedMarker.location?.lga)}</span>
                                    </div>
                                    <div className="d-flex align-items-center text-muted fs-8 mb-3">
                                      <i className="ki-outline ki-abstract-26 fs-7 me-1"></i>
                                      <span>{getMeasurementDisplay(selectedMarker)}</span>
                                    </div>
                                    <button className="btn btn-sm btn-primary w-100 py-2" onClick={() => openPermitModal(selectedMarker)}>View Full Details</button>
                                  </div>
                                </InfoWindow>
                              )}
                            </GoogleMap>
                          </LoadScript>
                        </div>
                      </div>

                    </div>

                    {/* Footer added here */}
                    <div className="app-footer py-10 mt-10 border-top">
                      <div className="text-center">
                        <span className="text-muted fw-semibold me-1">2026©</span>
                        <a href="https://edsaa.com.ng/" target="_blank" rel="noopener noreferrer" className="text-gray-800 text-hover-primary fw-bold">EDSAA - ADERA</a>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CREATE ADVERTISEMENT MODAL */}
      {showCombinedModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered mw-900px modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h3 className="modal-title fw-bold">Create Advertisement</h3>
                <button className="btn-close" onClick={() => setShowCombinedModal(false)}></button>
              </div>
              <div className="modal-body">
                {formErrors.submit && <div className="alert alert-danger">{formErrors.submit}</div>}

                <div className="row g-5">
                  <div className="col-md-6">
                    <h6 className="fw-bold text-gray-700 mb-3"><i className="ki-outline ki-user fs-2 me-1"></i> Client Information</h6>
                    <div className="bg-light p-4 rounded border mb-5">
                      <div className="d-flex gap-4 mb-3 border-bottom pb-3">
                        <div className="form-check">
                          <input className="form-check-input" type="radio" checked={!isExistingClient} onChange={() => setIsExistingClient(false)} />
                          <label className="form-check-label fw-bold">New Client</label>
                        </div>
                        <div className="form-check">
                          <input className="form-check-input" type="radio" checked={isExistingClient} onChange={() => setIsExistingClient(true)} />
                          <label className="form-check-label fw-bold">Existing Client</label>
                        </div>
                      </div>

                      {isExistingClient ? (
                        <div className="position-relative">
                          <label className="form-label required fw-semibold">Search Client</label>
                          <div className="input-group">
                            <input type="text" className={`form-control ${formErrors.client ? 'is-invalid' : ''}`}
                              placeholder="Name, Phone, or Email" value={clientSearchQuery}
                              onChange={(e) => setClientSearchQuery(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleClientSearch()} />
                            <button className="btn btn-primary" onClick={handleClientSearch} disabled={isSearchingClient}>
                              {isSearchingClient ? <span className="spinner-border spinner-border-sm"></span> : <i className="ki-outline ki-magnifier fs-2"></i>}
                            </button>
                          </div>
                          {formErrors.client && <div className="text-danger small mt-1">{formErrors.client}</div>}
                          {showClientDropdown && clientSearchResults.length > 0 && (
                            <div className="position-absolute w-100 bg-white shadow-lg border rounded mt-1 client-search-dropdown">
                              {clientSearchResults.map(c => (
                                <div key={c.id} className="p-2 border-bottom cursor-pointer" onClick={() => selectExistingClient(c)}>
                                  <div className="fw-bold">{c.company?.name || `${c.individual?.firstName} ${c.individual?.lastName}`}</div>
                                  <div className="small text-muted">{c.company?.phoneNumber || c.individual?.phoneNumber}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {adData.selectedClientId && <div className="alert alert-success mt-2 py-2 small"><i className="ki-outline ki-check-circle me-1"></i> Client Selected</div>}
                        </div>
                      ) : (
                        <>
                          <div className="d-flex gap-2 mb-3">
                            <button className={`btn btn-sm flex-grow-1 ${newClientCategory === 'Individual' ? 'btn-primary' : 'btn-light'}`} onClick={() => setNewClientCategory('Individual')}>Individual</button>
                            <button className={`btn btn-sm flex-grow-1 ${newClientCategory === 'Company' ? 'btn-primary' : 'btn-light'}`} onClick={() => setNewClientCategory('Company')}>Company</button>
                          </div>
                          {newClientCategory === 'Individual' ? (
                            <>
                              <div className="row mb-2">
                                <div className="col-6"><input type="text" className={`form-control ${formErrors.firstName ? 'is-invalid' : ''}`} name="firstName" placeholder="First Name" value={adData.firstName} onChange={handleInputChange} /></div>
                                <div className="col-6"><input type="text" className={`form-control ${formErrors.lastName ? 'is-invalid' : ''}`} name="lastName" placeholder="Last Name" value={adData.lastName} onChange={handleInputChange} /></div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-6"><input type="email" className={`form-control ${formErrors.email ? 'is-invalid' : ''}`} name="email" placeholder="Email" value={adData.email} onChange={handleInputChange} /></div>
                                <div className="col-6"><input type="tel" className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`} name="phone" placeholder="Phone" value={adData.phone} onChange={handleInputChange} /></div>
                              </div>
                              <select className="form-select" name="gender" value={adData.gender} onChange={handleInputChange}>
                                <option>Male</option><option>Female</option>
                              </select>
                            </>
                          ) : (
                            <>
                              <input type="text" className={`form-control mb-2 ${formErrors.companyName ? 'is-invalid' : ''}`} name="companyName" placeholder="Company Name" value={adData.companyName} onChange={handleInputChange} />
                              <input type="email" className={`form-control mb-2 ${formErrors.email ? 'is-invalid' : ''}`} name="email" placeholder="Company Email" value={adData.email} onChange={handleInputChange} />
                              <input type="tel" className={`form-control mb-2 ${formErrors.phone ? 'is-invalid' : ''}`} name="phone" placeholder="Company Phone" value={adData.phone} onChange={handleInputChange} />
                              <input type="text" className={`form-control mb-2 ${formErrors.companyAddress ? 'is-invalid' : ''}`} name="companyAddress" placeholder="Address" value={adData.companyAddress} onChange={handleInputChange} />
                            </>
                          )}
                        </>
                      )}
                    </div>

                    <h6 className="fw-bold text-gray-700 mb-3">Basic Info</h6>
                    <div className="bg-light p-4 rounded border">
                      <input type="text" className="form-control mb-3" name="title" value={adData.title} readOnly placeholder="Auto-generated Title" />
                      <textarea className="form-control mb-3" name="description" value={adData.description} readOnly rows="2" placeholder="Auto-generated Description"></textarea>
                      <label className="form-label required small fw-bold">Address</label>
                      <input type="text" className={`form-control mb-3 ${formErrors.address ? 'is-invalid' : ''}`} name="address" value={adData.address} onChange={handleInputChange} placeholder="e.g. Ring Road" />
                      <label className="form-label small fw-bold">Image</label>
                      <input type="file" className="form-control" name="imageFile" accept="image/*" onChange={handleInputChange} />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <h6 className="fw-bold text-gray-700 mb-3"><i className="ki-outline ki-category fs-2 me-1"></i> Ad Details</h6>
                    <div className="bg-light p-4 rounded border mb-5">
                      <div className="mb-3">
                        <label className="form-label required fw-bold">Zone (Location)</label>
                        <select className={`form-select ${formErrors.zone ? 'is-invalid' : ''}`} name="zoneId" value={adData.zoneId} onChange={handleInputChange}>
                          <option value="">Select Zone</option>
                          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                        {formErrors.zone && <div className="invalid-feedback">{formErrors.zone}</div>}
                      </div>

                      <div className="mb-3">
                        <label className="form-label required fw-bold">Category</label>
                        <select className={`form-select ${formErrors.category ? 'is-invalid' : ''}`} name="categoryId" value={adData.categoryId} onChange={handleInputChange}>
                          <option value="">Select Category</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        {formErrors.category && <div className="invalid-feedback">{formErrors.category}</div>}
                      </div>

                      <div className="mb-3">
                        <label className="form-label required fw-bold">Type</label>
                        <select className={`form-select ${formErrors.type ? 'is-invalid' : ''}`} name="typeId" value={adData.typeId} onChange={handleInputChange}
                          disabled={!adData.categoryId}>
                          <option value="">{adData.categoryId ? 'Select Type' : 'Select Category first'}</option>
                          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                        {formErrors.type && <div className="invalid-feedback">{formErrors.type}</div>}
                      </div>

                      <div className="mb-3">
                        <label className="form-label required fw-bold">Size</label>
                        <select className={`form-select ${formErrors.size ? 'is-invalid' : ''}`} name="sizeName" value={adData.sizeName} onChange={handleInputChange}
                          disabled={!adData.typeId}>
                          <option value="">{adData.typeId ? (availableSizes.length ? 'Select Size' : 'No sizes for this combination') : 'Select Type first'}</option>
                          {availableSizes.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {formErrors.size && <div className="invalid-feedback">{formErrors.size}</div>}
                      </div>

                      <div className="row">
                        <div className="col-6">
                          <label className="form-label fw-bold">No. of Faces</label>
                          <input type="number" className="form-control" name="faces" value={adData.faces} onChange={handleInputChange} min="1" />
                        </div>
                        <div className="col-6">
                          <label className="form-label fw-bold">Duration (Years)</label>
                          <input type="number" className="form-control" name="duration" value={adData.duration} onChange={handleInputChange} min="1" />
                        </div>
                      </div>

                      <div className={`mt-3 p-3 rounded ${adData.lookUpId ? 'bg-success bg-opacity-10 border border-success' : 'bg-warning bg-opacity-10 border border-warning'}`}>
                        {adData.lookUpId
                          ? <div className="text-success fw-bold fs-5">Total: {formatCurrency(adData.price)}</div>
                          : <div className="text-warning fw-bold small"><i className="ki-outline ki-warning fs-4 me-1"></i>
                              {adData.sizeName ? 'No pricing config for this combination' : 'Complete all selections to calculate price'}
                            </div>
                        }
                      </div>
                      {formErrors.lookUpId && <div className="text-danger small mt-1">{formErrors.lookUpId}</div>}
                    </div>

                    <h6 className="fw-bold text-gray-700 mb-3">Map Location</h6>
                    <div className="bg-light p-4 rounded border">
                      <label className="form-label required fw-bold">LGA</label>
                      <select className={`form-select mb-3 ${formErrors.lga ? 'is-invalid' : ''}`} name="lga" value={adData.lga} onChange={handleInputChange}>
                        <option value="">Select LGA</option>
                        {EDO_LGAS.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                      <div className="row">
                        <div className="col-6 mb-2"><input type="text" className="form-control" name="latitude" placeholder="Latitude" value={adData.latitude} onChange={handleInputChange} /></div>
                        <div className="col-6 mb-2"><input type="text" className="form-control" name="longitude" placeholder="Longitude" value={adData.longitude} onChange={handleInputChange} /></div>
                      </div>
                      <input type="text" className="form-control" name="altitude" placeholder="Altitude (optional)" value={adData.altitude} onChange={handleInputChange} />
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-3 mt-5">
                  <button className="btn btn-light" onClick={() => setShowCombinedModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleSubmit} disabled={savingAd || !adData.lookUpId}>
                    {savingAd ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</> : 'Create Advertisement'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PERMIT MODAL */}
      {showPermitModal && permitData && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered mw-900px">
            <div className="modal-content rounded shadow-lg">
              <div className="modal-header border-0 pb-0 position-relative">
                <div className="w-100 text-center mb-2">
                  <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                    <img src="assets/edo.png" alt="Edo Logo" className="h-60px" />
                    <img src="assets/edsal.png" alt="EDSAL Logo" className="h-60px" />
                  </div>
                  <h3 className="fw-bold text-uppercase text-success mb-1">Edo State Signage And Advertisement Agency</h3>
                  <p className="text-muted small mb-3">Block A (Palm House), 6th Floor, State Secretariat Complex, Sapele Road, Benin City, Edo State, Nigeria.</p>
                  <div className="separator separator-dashed my-3"></div>
                  <h4 className="fw-bold text-uppercase text-success mb-0">Advertising Permit</h4>
                </div>
                <button type="button" className="btn btn-sm btn-icon position-absolute top-0 end-0 m-3" onClick={() => setShowPermitModal(false)}>
                  <i className="ki-outline ki-cross fs-1"></i>
                </button>
              </div>
              <div className="modal-body py-5 px-lg-10">
                <div className="d-flex justify-content-center mb-8">
                  <div className="rounded overflow-hidden shadow-sm border" style={{ width: '500px', height: '250px' }}>
                    <img src={permitData.imageUrl} alt="Ad Preview" className="w-100 h-100" style={{ objectFit: 'cover' }}
                      onError={(e) => { e.target.src = "assets/bill2.jpg"; }} />
                  </div>
                </div>
                <div className="row g-5">
                  <div className="col-md-6">
                    <h6 className="fw-bold text-gray-700 mb-3">Client Information</h6>
                    <div className="bg-light p-4 rounded border">
                      <div className="mb-2"><strong>Name:</strong> {permitData.clientName}</div>
                      <div className="mb-2"><strong>Email:</strong> {permitData.clientEmail?.toLowerCase()}</div>
                      <div className="mb-2"><strong>Phone:</strong> {permitData.clientPhone}</div>
                      <div className="mb-0"><strong>Address:</strong> {permitData.clientAddress}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold text-gray-700 mb-3">Permit Details</h6>
                    <div className="bg-light p-4 rounded border">
                      <div className="mb-2"><strong>Amount:</strong> {permitData.amount}</div>
                      <div className="mb-2"><strong>Category:</strong> {permitData.category}</div>
                      <div className="mb-2"><strong>Type:</strong> {permitData.type}</div>
                      <div className="mb-0"><strong>Expiry:</strong> {permitData.expiryDate}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold text-gray-700 mb-3">Advertisement Details</h6>
                    <div className="bg-light p-4 rounded border">
                      <div className="mb-2"><strong>Title:</strong> {permitData.adTitle}</div>
                      <div className="mb-2"><strong>Size:</strong> {permitData.adSize}</div>
                      <div className="mb-2"><strong>Measurement:</strong> {permitData.measurement}</div>
                      <div className="mb-2"><strong>Location:</strong> {permitData.adLocation}</div>
                      <div className="mb-0"><strong>Address:</strong> {permitData.adAddress}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold text-gray-700 mb-3">Agent & Status</h6>
                    <div className="bg-light p-4 rounded border">
                      <div className="mb-2"><strong>Status:</strong> <span className={`badge ${permitData.status === 'Successful' ? 'badge-success' : 'badge-warning'}`}>{permitData.status}</span></div>
                      <div className="mb-2"><strong>Agent:</strong> {permitData.agentName}</div>
                      <div className="mb-2"><strong>Email:</strong> {permitData.agentEmail}</div>
                      <div className="mb-0"><strong>Phone:</strong> {permitData.agentPhone}</div>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-between mt-8">
                  <button className="btn btn-success btn-lg px-5">Download Permit</button>
                  <button className="btn btn-light btn-lg px-5" onClick={() => setShowPermitModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdManagement;